import type { AdminNotification } from "@/services/adminNotificationService";
import { adminBookingDetailPath } from "@/components/handshake";
import { notificationMetaString } from "./notificationMetadata";

export type NotificationLinkLabel =
  | "openPayment"
  | "openBooking"
  | "openHandshake"
  | "openTicket"
  | "openChat"
  | "openDevice"
  | "openStructure";

export interface NotificationLink {
  key: string;
  to: string;
  label: NotificationLinkLabel;
}

function addLink(
  links: NotificationLink[],
  seen: Set<string>,
  to: string,
  key: string,
  label: NotificationLinkLabel,
): void {
  if (!to.startsWith("/") || seen.has(to)) {
    return;
  }
  seen.add(to);
  links.push({ key, to, label });
}

export function getNotificationLinks(
  notification: AdminNotification,
): NotificationLink[] {
  const links: NotificationLink[] = [];
  const seen = new Set<string>();
  const meta = notification.metadata_json;

  const bookingId =
    notificationMetaString(meta, "booking_id") ??
    (notification.target_type === "booking" ? notification.target_id : undefined);
  const sellerUserId = notificationMetaString(meta, "seller_user_id");
  const paymentId =
    notificationMetaString(meta, "external_payment_id") ??
    (notification.target_type === "external_payment"
      ? notification.target_id
      : undefined);
  const sessionId =
    notification.target_type === "handshake_session"
      ? notification.target_id
      : undefined;
  const ticketId =
    notificationMetaString(meta, "ticket_id") ??
    (notification.target_type === "support_ticket"
      ? notification.target_id
      : undefined);
  const chatId =
    notificationMetaString(meta, "chat_id") ??
    (notification.target_type === "customer_support_chat"
      ? notification.target_id
      : undefined);
  const houseId = notificationMetaString(meta, "house_id");
  const targetId = notification.target_id;

  if (sessionId) {
    addLink(
      links,
      seen,
      `/admin/handshake/${sessionId}`,
      `handshake-${sessionId}`,
      "openHandshake",
    );
  }

  if (bookingId && notification.event_type === "booking.created") {
    addLink(
      links,
      seen,
      adminBookingDetailPath(bookingId, sellerUserId),
      `booking-${bookingId}`,
      "openBooking",
    );
  }

  if (paymentId) {
    addLink(
      links,
      seen,
      `/admin/payments/external-payments?paymentId=${paymentId}`,
      `payment-${paymentId}`,
      "openPayment",
    );
  }

  if (ticketId) {
    addLink(
      links,
      seen,
      `/admin/tickets/${ticketId}`,
      `ticket-${ticketId}`,
      "openTicket",
    );
  }

  if (chatId) {
    addLink(
      links,
      seen,
      `/admin/chats?chatId=${chatId}`,
      `chat-${chatId}`,
      "openChat",
    );
  }

  if (notification.target_type === "device" && targetId) {
    addLink(
      links,
      seen,
      `/devices?deviceId=${targetId}`,
      `device-${targetId}`,
      "openDevice",
    );
  }

  if (notification.target_type === "reader" && targetId) {
    addLink(
      links,
      seen,
      `/readers?readerId=${targetId}`,
      `reader-${targetId}`,
      "openDevice",
    );
  }

  if (houseId) {
    addLink(
      links,
      seen,
      `/houses/${houseId}`,
      `house-${houseId}`,
      "openStructure",
    );
  }

  return links;
}
